import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, FileSpreadsheet, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

// EU language codes mapping to Excel column names
const LANGUAGE_COLUMNS: Record<string, string[]> = {
  'bg': ['BG'],
  'cs': ['CS'],
  'da': ['DA'],
  'de': ['DE'],
  'el': ['EL'],
  'en': ['EN'],
  'es': ['ES'],
  'et': ['ET'],
  'fi': ['FI'],
  'fr': ['FR'],
  'ga': ['GA'],
  'hr': ['HR'],
  'hu': ['HU'],
  'it': ['IT'],
  'lt': ['LT'],
  'lv': ['LV'],
  'mt': ['MT'],
  'nl': ['NL'],
  'pl': ['PL'],
  'pt': ['PT'],
  'ro': ['RO'],
  'sk': ['SK'],
  'sl': ['SL'],
  'sv': ['SV']
};

interface ImportStats {
  totalRows: number;
  processedRows: number;
  totalCpvCodes: number;
  insertedCpvCodes: number;
  totalTranslations: number;
  insertedTranslations: number;
  errors: number;
  status: 'idle' | 'reading' | 'processing' | 'complete' | 'error';
  errorMessage?: string;
}

// Extract CPV category from code (first 2 digits)
const getCpvCategory = (code: string): string => {
  const division = code.substring(0, 2);
  const categories: Record<string, string> = {
    '03': 'Prodotti agricoli',
    '09': 'Prodotti petroliferi e energia',
    '14': 'Prodotti minerari',
    '15': 'Prodotti alimentari',
    '16': 'Macchine agricole',
    '18': 'Abbigliamento',
    '19': 'Cuoio e tessuti',
    '22': 'Stampati e prodotti affini',
    '24': 'Prodotti chimici',
    '30': 'Macchine per ufficio',
    '31': 'Apparecchiature elettriche',
    '32': 'Apparecchi radio e televisione',
    '33': 'Apparecchiature mediche',
    '34': 'Veicoli',
    '35': 'Sicurezza e difesa',
    '37': 'Strumenti musicali e sportivi',
    '38': 'Strumenti di laboratorio',
    '39': 'Mobili',
    '41': 'Acqua raccolta e depurata',
    '42': 'Macchinari industriali',
    '43': 'Macchinari per miniere',
    '44': 'Materiali da costruzione',
    '45': 'Lavori di costruzione',
    '48': 'Software',
    '50': 'Servizi di riparazione',
    '51': 'Servizi di installazione',
    '55': 'Servizi alberghieri',
    '60': 'Servizi di trasporto',
    '63': 'Servizi di supporto trasporti',
    '64': 'Servizi postali e telecomunicazioni',
    '65': 'Servizi pubblici',
    '66': 'Servizi finanziari',
    '70': 'Servizi immobiliari',
    '71': 'Servizi architettura e ingegneria',
    '72': 'Servizi informatici',
    '73': 'Ricerca e sviluppo',
    '75': 'Servizi amministrazione pubblica',
    '76': 'Servizi petrolio e gas',
    '77': 'Servizi agricoli e forestali',
    '79': 'Servizi alle imprese',
    '80': 'Servizi istruzione',
    '85': 'Servizi sanitari e sociali',
    '90': 'Servizi fognatura e rifiuti',
    '92': 'Servizi ricreativi e culturali',
    '98': 'Altri servizi comunitari'
  };
  return categories[division] || 'Altro';
};

// Extract CPV division from code (first 2 digits description) - NOT USED, column is generated
// const getCpvDivision = (code: string): string => {
//   return code.substring(0, 2);
// };

export default function AdminCpv() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [stats, setStats] = useState<ImportStats>({
    totalRows: 0,
    processedRows: 0,
    totalCpvCodes: 0,
    insertedCpvCodes: 0,
    totalTranslations: 0,
    insertedTranslations: 0,
    errors: 0,
    status: 'idle'
  });

  const processExcelFile = async (file: File) => {
    setStats({
      totalRows: 0,
      processedRows: 0,
      totalCpvCodes: 0,
      insertedCpvCodes: 0,
      totalTranslations: 0,
      insertedTranslations: 0,
      errors: 0,
      status: 'reading'
    });

    try {
      const { readSheetRows } = await import('@/lib/excel');
      const data = await readSheetRows(file);
      
      if (data.length < 2) {
        throw new Error('File vuoto o senza dati');
      }

      // First row is headers
      const headers = (data[0] as unknown[]).map(h => String(h || ''));
      const rows = data.slice(1) as unknown[][];
      
      // Find CODE column
      const codeColumnIndex = headers.findIndex(h => 
        h?.toString().toUpperCase() === 'CODE' || 
        h?.toString().toUpperCase() === 'CPV_CODE' ||
        h?.toString().toUpperCase() === 'CPV' ||
        h?.toString().match(/^\d+/)
      );

      if (codeColumnIndex === -1) {
        throw new Error('Colonna CODE non trovata');
      }

      // Map language columns to their indices
      const languageIndices: Record<string, number> = {};
      Object.entries(LANGUAGE_COLUMNS).forEach(([langCode, possibleNames]) => {
        const idx = headers.findIndex(h => {
          const header = h?.toString().toUpperCase();
          return possibleNames.some(name => header === name || header?.includes(name));
        });
        if (idx !== -1) {
          languageIndices[langCode] = idx;
        }
      });

      console.log('Found language columns:', languageIndices);

      setStats(prev => ({
        ...prev,
        totalRows: rows.length,
        status: 'processing'
      }));

      // Process in batches
      const batchSize = 100;
      const translationBatchSize = 500;
      const cpvCodeBatchSize = 500;
      let allTranslations: Array<{ code: string; language: string; description: string }> = [];
      let allCpvCodes: Array<{ code: string; description: string; category: string }> = [];
      const processedCodes = new Set<string>();

      for (let i = 0; i < rows.length; i += batchSize) {
        const batch = rows.slice(i, i + batchSize);
        
        batch.forEach(row => {
          const rawCode = row[codeColumnIndex];
          const code = rawCode != null ? String(rawCode).trim() : '';
          if (!code || !/^\d{8}(-\d)?$/.test(code)) return; // Skip invalid CPV codes

          // Collect CPV codes for cpv_codes table (use EN description as default)
          if (!processedCodes.has(code)) {
            processedCodes.add(code);
            const enIndex = languageIndices['en'];
            const enDesc = enIndex !== undefined && row[enIndex] != null 
              ? String(row[enIndex]).trim() 
              : '';
            
            if (enDesc) {
              allCpvCodes.push({
                code,
                description: enDesc,
                category: getCpvCategory(code)
              });
            }
          }

          // Collect translations for all languages
          Object.entries(languageIndices).forEach(([langCode, colIndex]) => {
            const rawDesc = row[colIndex];
            const description = rawDesc != null ? String(rawDesc).trim() : '';
            if (description) {
              allTranslations.push({
                code,
                language: langCode,
                description
              });
            }
          });
        });

        setStats(prev => ({
          ...prev,
          processedRows: Math.min(i + batchSize, rows.length),
          totalCpvCodes: allCpvCodes.length,
          totalTranslations: allTranslations.length
        }));
      }

      // First, insert CPV codes in batches
      let insertedCpvCount = 0;
      let errorCount = 0;

      for (let i = 0; i < allCpvCodes.length; i += cpvCodeBatchSize) {
        const batch = allCpvCodes.slice(i, i + cpvCodeBatchSize);
        
        const { error } = await supabase
          .from('cpv_codes')
          .upsert(batch, { 
            onConflict: 'code',
            ignoreDuplicates: false 
          });

        if (error) {
          console.error('CPV codes batch insert error:', error);
          errorCount += batch.length;
        } else {
          insertedCpvCount += batch.length;
        }

        setStats(prev => ({
          ...prev,
          insertedCpvCodes: insertedCpvCount,
          errors: errorCount
        }));
      }

      // Then insert translations in batches
      let insertedTranslationsCount = 0;

      for (let i = 0; i < allTranslations.length; i += translationBatchSize) {
        const batch = allTranslations.slice(i, i + translationBatchSize);
        
        const { error } = await supabase
          .from('cpv_translations')
          .upsert(batch, { 
            onConflict: 'code,language',
            ignoreDuplicates: false 
          });

        if (error) {
          console.error('Translations batch insert error:', error);
          errorCount += batch.length;
        } else {
          insertedTranslationsCount += batch.length;
        }

        setStats(prev => ({
          ...prev,
          insertedTranslations: insertedTranslationsCount,
          errors: errorCount
        }));
      }

      setStats(prev => ({
        ...prev,
        status: 'complete'
      }));

      toast({
        title: "Importazione completata",
        description: `${insertedCpvCount} codici CPV e ${insertedTranslationsCount} traduzioni importate`,
      });

    } catch (error) {
      console.error('Error processing file:', error);
      const message = error instanceof Error ? error.message : 'Errore sconosciuto';
      setStats(prev => ({
        ...prev,
        status: 'error',
        errorMessage: message
      }));
      toast({
        title: "Errore importazione",
        description: message,
        variant: "destructive"
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processExcelFile(file);
    }
  };

  const progress = stats.totalRows > 0 
    ? Math.round((stats.processedRows / stats.totalRows) * 100) 
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Importazione CPV Multilingua</h1>
          <p className="text-muted-foreground mt-2">
            Carica il file Excel con i codici CPV e le descrizioni in tutte le lingue UE
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Carica File Excel
            </CardTitle>
            <CardDescription>
              Il file deve contenere una colonna CODE con i codici CPV e colonne per ogni lingua (BG, CS, DA, DE, EL, EN, ES, ET, FI, FR, GA, HR, HU, IT, LT, LV, MT, NL, PL, PT, RO, SK, SL, SV)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={() => fileInputRef.current?.click()}
                disabled={stats.status === 'reading' || stats.status === 'processing'}
                className="gap-2"
              >
                {stats.status === 'reading' || stats.status === 'processing' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5" />
                )}
                Seleziona File Excel
              </Button>
            </div>

            {stats.status !== 'idle' && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Stato:</span>
                  <Badge variant={
                    stats.status === 'complete' ? 'default' :
                    stats.status === 'error' ? 'destructive' :
                    'secondary'
                  }>
                    {stats.status === 'reading' && 'Lettura file...'}
                    {stats.status === 'processing' && 'Elaborazione...'}
                    {stats.status === 'complete' && (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Completato
                      </span>
                    )}
                    {stats.status === 'error' && (
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Errore
                      </span>
                    )}
                  </Badge>
                </div>

                {stats.status === 'processing' && (
                  <div className="space-y-2">
                    <Progress value={progress} className="h-2" />
                    <p className="text-sm text-muted-foreground text-center">
                      {stats.processedRows} / {stats.totalRows} righe elaborate
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Righe totali:</span>
                    <span className="font-medium">{stats.totalRows.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Codici CPV trovati:</span>
                    <span className="font-medium">{stats.totalCpvCodes.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Codici CPV inseriti:</span>
                    <span className="font-medium text-green-600">{stats.insertedCpvCodes.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Traduzioni trovate:</span>
                    <span className="font-medium">{stats.totalTranslations.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Traduzioni inserite:</span>
                    <span className="font-medium text-green-600">{stats.insertedTranslations.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Errori:</span>
                    <span className={`font-medium ${stats.errors > 0 ? 'text-red-600' : ''}`}>
                      {stats.errors.toLocaleString()}
                    </span>
                  </div>
                </div>

                {stats.errorMessage && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                    {stats.errorMessage}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Istruzioni</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <ol className="list-decimal list-inside space-y-2">
              <li>Estrai il file ZIP contenente il foglio Excel CPV 2008</li>
              <li>Apri il file .xls e salvalo come .xlsx (formato più moderno)</li>
              <li>Carica il file usando il pulsante qui sopra</li>
              <li>Attendi il completamento dell'importazione</li>
            </ol>
            <p className="text-muted-foreground">
              Il sistema importerà automaticamente le descrizioni in tutte le 24 lingue ufficiali UE.
              I codici CPV esistenti verranno aggiornati con le nuove traduzioni.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
